import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.24 1.4-1.7 4.1-5.4 4.1-3.2 0-5.9-2.7-5.9-6s2.7-6 5.9-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.6 14.6 2.6 12 2.6 6.8 2.6 2.6 6.8 2.6 12S6.8 21.4 12 21.4c6.9 0 9.4-4.9 9.4-9.2 0-.6-.1-1.1-.2-1.6H12z" />
  </svg>
);

const AuthModal = () => {
  const { authModalOpen, authModalMode, authIntent, setAuthModalMode, closeAuth, login, register, startGoogleLogin } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "backer" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authModalOpen) {
      setForm({ email: "", password: "", name: "", role: "backer" });
      setError("");
      setBusy(false);
    }
  }, [authModalOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const result = authModalMode === "login"
      ? await login(form.email, form.password)
      : await register({ email: form.email, password: form.password, name: form.name, role: form.role });
    setBusy(false);
    if (result.ok) {
      toast.success(authModalMode === "login" ? "Welcome back!" : "Account created — welcome to Dwinov");
      closeAuth();
    } else {
      setError(result.error || "Authentication failed");
    }
  };

  return (
    <Dialog open={authModalOpen} onOpenChange={(o) => !o && closeAuth()}>
      <DialogContent className="sm:max-w-[440px] rounded-2xl p-0 overflow-hidden" data-testid="auth-modal">
        <div className="p-8">
          <div className="mb-6">
            <img src="/dwinov-logo.webp" alt="Dwinov" className="h-10 w-auto object-contain" />
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl tracking-tight">
              {authModalMode === "login" ? "Welcome back." : "Create your account."}
            </DialogTitle>
            <DialogDescription className="text-navy-500">
              {authIntent ? authIntent : (authModalMode === "login" ? "Sign in to continue." : "Join the earliest community of Dwinov creators and backers.")}
            </DialogDescription>
          </DialogHeader>

          <Button
            type="button"
            variant="outline"
            onClick={startGoogleLogin}
            data-testid="auth-google-btn"
            className="mt-6 w-full h-11 rounded-2xl border-warm-200 hover:border-saffron-200 hover:bg-cream-100 gap-2"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs uppercase tracking-wider text-slate-400">or</span>
            <Separator className="flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {authModalMode === "register" && (
              <div>
                <Label htmlFor="auth-name">Name</Label>
                <Input
                  id="auth-name" data-testid="auth-name-input" required
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl border-warm-200"
                />
              </div>
            )}
            <div>
              <Label htmlFor="auth-email">Email</Label>
              <Input
                id="auth-email" data-testid="auth-email-input" required type="email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 h-11 rounded-xl border-warm-200"
              />
            </div>
            <div>
              <Label htmlFor="auth-password">Password</Label>
              <Input
                id="auth-password" data-testid="auth-password-input" required type="password" minLength={6}
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 h-11 rounded-xl border-warm-200"
              />
            </div>

            {authModalMode === "register" && (
              <div>
                <Label>I want to</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {[
                    { v: "backer", l: "Back projects" },
                    { v: "creator", l: "Launch projects" }
                  ].map((r) => (
                    <button
                      type="button" key={r.v}
                      onClick={() => setForm({ ...form, role: r.v })}
                      data-testid={`auth-role-${r.v}`}
                      className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.role === r.v ? "border-saffron-500 bg-saffron-50 text-saffron-700" : "border-warm-200 text-navy-700 hover:border-saffron-200"}`}
                    >
                      {r.l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700" data-testid="auth-error">
                {error}
              </div>
            )}

            <Button
              type="submit" disabled={busy} data-testid="auth-submit-btn"
              className="w-full h-11 rounded-2xl bg-saffron-600 hover:bg-saffron-700 text-white hover:scale-[1.02] active:scale-95 transition-all"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (authModalMode === "login" ? "Sign in" : "Create account")}
            </Button>
          </form>

          <div className="mt-5 text-center text-sm text-navy-500">
            {authModalMode === "login" ? (
              <>Don't have an account?{" "}
                <button data-testid="auth-switch-register" onClick={() => setAuthModalMode("register")} className="text-saffron-600 font-medium hover:underline">Create one</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button data-testid="auth-switch-login" onClick={() => setAuthModalMode("login")} className="text-saffron-600 font-medium hover:underline">Sign in</button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
